import { useRef, useState } from 'react';
import {
    Box,
    Flex,
    HStack,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
} from '@chakra-ui/react';
import { CloseIcon, SearchIcon } from '@chakra-ui/icons';
import { LuEye } from 'react-icons/lu';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'hooks/useCustomQuery';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import AGGridBase from 'components/tables/AGGrid/AGGridBase';
import {
    boxSearchStyling,
    searchStyling,
} from 'components/tables/AGGrid/agGridStyles';
import { Button, Text } from '@/components/v4';
import { getNumberBlockOrders } from 'API/NumberBlocks/getNumberBlockOrders';
import { calculateLastNumber } from 'utils/numberBlockUtils';
import OrderStatusPill from './OrderStatusPill';
import OrderStatuses, {
    ACTIVE_ORDER_LABEL,
    isActiveOrder,
} from './orderStatuses';

function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const statusFilterCellRenderer = (params) => {
    if (params?.value === '(Select All)') return '(Select All)';
    const displayName =
        OrderStatuses[params?.value]?.displayedName ?? params?.value;
    return <Text isTruncated>{displayName}</Text>;
};
statusFilterCellRenderer.displayName = 'OrderStatusFilterCellRenderer';

function OrdersListView() {
    const tableRef = useRef(null);
    const navigate = useNavigate();
    const { requiredScope, currentScope } = useSelector((state) => state.login);
    const { currentWholesaler, currentPartner, currentCompany } = useSelector(
        (state) => state.navigationLists,
    );
    const { darkMode } = useSelector((state) => state.settings);

    const {
        data: orders = [],
        isLoading,
        refetch,
    } = useQuery({
        queryKey: ['numberBlockOrders', requiredScope, currentScope],
        queryFn: async () => {
            const res = await getNumberBlockOrders({
                wholesalerID:
                    currentScope === 60 ? currentWholesaler : undefined,
                partnerID: currentScope === 40 ? currentPartner : undefined,
                companyID: currentScope === 20 ? currentCompany : undefined,
                scope: requiredScope,
                pageSize: 1000, // maximum page size that global admin can request;
            });
            return res.data;
        },
        staleTime: 0,
        refetchOnWindowFocus: false,
    });

    const columns = [
        {
            headerName: 'Company Name',
            field: 'companyName',
            flex: 1,
            minWidth: 150,
            suppressHeaderMenuButton: true,
        },
        {
            headerName: 'First Number',
            field: 'first',
            flex: 1,
            minWidth: 150,
            suppressHeaderMenuButton: true,
            valueFormatter: (params) =>
                params.value ? `+${params.value}` : '',
        },
        {
            headerName: 'Submission Date',
            field: 'createTS',
            flex: 1,
            minWidth: 150,
            suppressHeaderMenuButton: true,
            valueFormatter: (params) => formatDate(params.value),
        },
        {
            headerName: 'Status',
            field: 'status',
            flex: 1,
            minWidth: 150,
            wrapText: true,
            textOverflow: 'none',
            suppressHeaderMenuButton: true,
            filter: true,
            valueGetter: (params) => {
                const { status, uploadOrderID } = params?.data ?? {};
                if (isActiveOrder(status, uploadOrderID))
                    return ACTIVE_ORDER_LABEL;
                return OrderStatuses[status]?.displayedName ?? status;
            },
            valueFormatter: (params) =>
                OrderStatuses[params?.value]?.displayedName ?? params?.value,
            filterValueGetter: (params) => {
                const { status, uploadOrderID } = params?.data ?? {};
                return isActiveOrder(status, uploadOrderID)
                    ? ACTIVE_ORDER_LABEL
                    : status;
            },
            filterParams: { cellRenderer: statusFilterCellRenderer },
            cellRenderer: (params) => (
                <OrderStatusPill
                    status={params.data?.status}
                    uploadOrderID={params.data?.uploadOrderID}
                    errorMessage={params.data?.statusMessage}
                />
            ),
        },
        {
            headerName: 'Tenant ID',
            field: 'tenantId',
            flex: 1,
            hide: true,
            suppressHeaderMenuButton: true,
        },
        {
            headerName: 'Action',
            headerClass: 'ag-action-header-center',
            lockPosition: 'right',
            maxWidth: 100,
            sortable: false,
            filter: false,
            suppressMovable: true,
            lockVisible: true,
            suppressColumnsToolPanel: true,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            resizable: false,
            cellStyle: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            },
            cellRenderer: (params) => (
                <IconButton
                    size="sm"
                    variant="ghost"
                    aria-label="View order"
                    icon={<LuEye />}
                    color={darkMode ? 'white' : 'gray.700'}
                    _hover={{
                        bg: darkMode ? 'whiteAlpha.200' : 'blackAlpha.100',
                    }}
                    _active={{
                        bg: darkMode ? 'whiteAlpha.300' : 'blackAlpha.200',
                    }}
                    _focus={{ boxShadow: 'none' }}
                    onClick={() =>
                        navigate(`/number-blocks/orders/${params.data.id}`, {
                            state: { order: params.data },
                        })
                    }
                />
            ),
        },
    ];

    return (
        <Box pt="15px">
            <AGGridMain
                stateIdentifier="numberBlockOrdersTable"
                gridRef={tableRef}
                tableData={{ columns, data: orders }}
                isLoading={isLoading}
                refetch={refetch}
                pagination={true}
                paginationPageSize={100}
                paginationPageSizeSelector={[50, 100, 250, 500, 1000]}
                suppressPaginationPanel={false}
                rowBuffer={20}
                suppressRowVirtualisation={false}
            />
        </Box>
    );
}

export function OrderDetailView({ order, onBack }) {
    const [filterText, setFilterText] = useState('');
    const tableRef = useRef(null);
    const navigate = useNavigate();
    const { darkMode } = useSelector((state) => state.settings);

    // Number Blocks → Search tab (default); Orders → Orders tab (same as Back)
    const goToNumberBlocks = () => navigate('/number-blocks');
    const goToOrders = () =>
        navigate('/number-blocks', { state: { openOrdersTab: true } });

    const columns = [
        {
            headerName: 'First',
            field: 'first',
            flex: 2,
            valueFormatter: (params) =>
                params.value ? `+${params.value}` : '',
        },
        {
            headerName: 'Last',
            field: 'last',
            flex: 2,
            valueFormatter: (params) =>
                params.value ? `+${params.value}` : '',
        },
        {
            headerName: 'Block Size',
            field: 'blockSize',
            flex: 1,
            type: 'numericColumn',
        },
        {
            headerName: 'Status',
            field: 'status',
            flex: 1,
            minWidth: 150,
            cellStyle: {
                overflow: 'visible',
                textOverflow: 'unset',
                display: 'flex',
                alignItems: 'center',
            },
            cellRenderer: (params) => (
                <OrderStatusPill
                    status={params.value}
                    uploadOrderID={params.data?.uploadOrderID}
                    errorMessage={params.data?.statusMessage}
                />
            ),
        },
    ];

    // No GET for a single order; derive the detail row from the clicked
    // /NumberBlock/Requests record passed in via `order`.
    const rows = order
        ? [
              {
                  first: order.first,
                  last: calculateLastNumber(order.first, order.blockSize),
                  blockSize: order.blockSize,
                  status: order.status,
                  uploadOrderID: order.uploadOrderID,
                  statusMessage: order.statusMessage,
              },
          ]
        : [];

    return (
        <Box>
            {/* Breadcrumb + back button */}
            <Flex justify="space-between" align="center" mb={6}>
                <HStack spacing={1} fontSize="sm" color="gray.500">
                    <Text
                        fontSize="sm"
                        color="gray.500"
                        cursor="pointer"
                        _hover={{ textDecoration: 'underline' }}
                        onClick={goToNumberBlocks}>
                        Number Blocks
                    </Text>
                    <Text fontSize="sm" color="gray.400">
                        /
                    </Text>
                    <Text
                        fontSize="sm"
                        color="gray.500"
                        cursor="pointer"
                        _hover={{ textDecoration: 'underline' }}
                        onClick={goToOrders}>
                        Orders
                    </Text>
                    <Text fontSize="sm" color="gray.400">
                        /
                    </Text>
                    <Text fontSize="sm" color="gray.700" fontWeight="medium">
                        Order Details
                    </Text>
                </HStack>
                <Button variant="outline" size="sm" onClick={onBack}>
                    Back to Orders List
                </Button>
            </Flex>

            {/* Table card */}
            <Box
                border="1px solid"
                borderColor="gray.200"
                borderRadius="lg"
                p={5}>
                {/* Card header */}
                <Flex justify="space-between" align="flex-start" mb={4}>
                    <Box>
                        <Text fontWeight="bold" fontSize="md">
                            Provisioned Continuous Sequences
                        </Text>
                    </Box>
                    <InputGroup sx={boxSearchStyling} maxW="240px">
                        <InputLeftElement pointerEvents="none" height="30px">
                            <SearchIcon color="gray.300" />
                        </InputLeftElement>
                        <Input
                            placeholder="Search number within range..."
                            value={filterText}
                            onInput={(e) => setFilterText(e.target.value)}
                            sx={searchStyling}
                            color={darkMode ? 'dark.textDark' : null}
                            background={darkMode ? 'dark.bgDark' : null}
                            borderColor={
                                darkMode ? 'dark.tcap-borderGray' : null
                            }
                        />
                        {filterText && (
                            <InputRightElement height="30px">
                                <CloseIcon
                                    className="cursor-pointer"
                                    onClick={() => setFilterText('')}
                                    color="gray.300"
                                />
                            </InputRightElement>
                        )}
                    </InputGroup>
                </Flex>

                {/* Table — sized to content, not the full page */}
                <AGGridBase
                    gridRef={tableRef}
                    tableData={{ columns, data: rows }}
                    defaultColDef={{ suppressHeaderMenuButton: true }}
                    quickFilterText={filterText}
                    pagination={false}
                    useFixedHeight={false}
                    domLayout={rows.length > 0 ? 'autoHeight' : 'normal'}
                    customHeight={rows.length === 0 ? '120px' : 'auto'}
                    overlayNoRowsTemplate="<span>No blocks in this order</span>"
                />
            </Box>
        </Box>
    );
}

/**
 * Orders tab content (the list). Clicking a row's action navigates to the
 * dedicated /number-blocks/orders/:orderId route (see OrderDetailsPage),
 * passing the row data via navigation state.
 */
export default function OrdersTab() {
    return <OrdersListView />;
}
